import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, serverTimestamp } from "firebase/firestore";
import {
  Badge,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ScienceIcon from "@mui/icons-material/Science";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { actorFrom, createMeta } from "../../utils/auditFields";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";

const isChemical = (item) => Boolean(item.sdsURL);
const itemKey = (item) => item.itemSKU || item.itemID || item.itemName;

const currency = (amount) =>
  `$${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const PartsCatalogRequest = ({ userInfo, userId }) => {
  const { selectedProperty, selectedClient, items } = useContext(PropertyContext);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | parts | chemicals
  const [cart, setCart] = useState({}); // key -> { item, count }
  const [cartOpen, setCartOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (items || [])
      .filter((item) => {
        if (filter === "chemicals") return isChemical(item);
        if (filter === "parts") return !isChemical(item);
        return true;
      })
      .filter((item) => {
        if (!term) return true;
        return (
          (item.itemName || "").toLowerCase().includes(term) ||
          (item.itemSKU || "").toLowerCase().includes(term)
        );
      })
      .sort((a, b) => (a.itemName || "").localeCompare(b.itemName || ""));
  }, [items, search, filter]);

  const cartLines = useMemo(() => Object.values(cart), [cart]);
  const cartCount = useMemo(
    () => cartLines.reduce((sum, l) => sum + l.count, 0),
    [cartLines]
  );
  const cartTotal = useMemo(
    () =>
      cartLines.reduce(
        (sum, l) => sum + Number(l.item.itemCost || 0) * l.count,
        0
      ),
    [cartLines]
  );

  const addToCart = (item) => {
    const key = itemKey(item);
    setCart((prev) => ({
      ...prev,
      [key]: { item, count: (prev[key]?.count || 0) + 1 },
    }));
  };

  const changeCount = (key, delta) => {
    setCart((prev) => {
      const line = prev[key];
      if (!line) return prev;
      const count = line.count + delta;
      if (count <= 0) {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: { ...line, count } };
    });
  };

  const removeLine = (key) => {
    setCart((prev) => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async () => {
    if (cartLines.length === 0 || !selectedProperty) return;
    setSubmitting(true);
    try {
      const { name } = actorFrom(userInfo);
      const lineItems = cartLines.map(({ item, count }) => ({
        itemName: item.itemName || "",
        itemSKU: item.itemSKU || null,
        itemCost: Number(item.itemCost) || 0,
        itemType: item.itemType || null,
        vendor: item.vendor || null,
        isChemical: isChemical(item),
        count,
      }));
      await addDoc(
        propertyCollection(selectedClient, selectedProperty, "partsRequests"),
        {
          items: lineItems,
          itemCount: cartCount,
          totalEstimated: cartTotal,
          status: "pending",
          ordered: false, // legacy compatibility with OrderRequests view
          requestedBy: userId,
          requestedByName: name,
          requestedAt: serverTimestamp(),
          ...createMeta(userInfo),
        }
      );
      navigate("/parts");
    } catch (err) {
      console.error("Failed to submit parts request:", err);
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 720, mx: "auto" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/parts")}>
          Back
        </Button>
        <Badge badgeContent={cartCount} color="primary">
          <Button
            variant="contained"
            startIcon={<ShoppingCartIcon />}
            onClick={() => setCartOpen(true)}
            disabled={cartCount === 0}
          >
            Cart
          </Button>
        </Badge>
      </Box>

      <Typography variant="h5" fontWeight={600} gutterBottom>
        Request Parts
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Search parts & chemicals…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <ToggleButtonGroup
        value={filter}
        exclusive
        size="small"
        onChange={(e, v) => v && setFilter(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="parts">Parts</ToggleButton>
        <ToggleButton value="chemicals">Chemicals</ToggleButton>
      </ToggleButtonGroup>

      <Stack spacing={1}>
        {filtered.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No items match your search.
          </Typography>
        ) : (
          filtered.map((item) => {
            const key = itemKey(item);
            const inCart = cart[key]?.count || 0;
            return (
              <Card
                key={key}
                variant="outlined"
                sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box flexGrow={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Typography variant="subtitle2" noWrap>
                      {item.itemName}
                    </Typography>
                    {isChemical(item) && (
                      <Chip
                        size="small"
                        color="info"
                        icon={<ScienceIcon />}
                        label="Chemical"
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {item.itemSKU ? `${item.itemSKU} · ` : ""}
                    {currency(item.itemCost)}
                  </Typography>
                </Box>
                {inCart > 0 ? (
                  <Box display="flex" alignItems="center">
                    <IconButton size="small" onClick={() => changeCount(key, -1)}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ minWidth: 24, textAlign: "center" }}>
                      {inCart}
                    </Typography>
                    <IconButton size="small" onClick={() => changeCount(key, 1)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addToCart(item)}
                  >
                    Add
                  </Button>
                )}
              </Card>
            );
          })
        )}
      </Stack>

      <Drawer
        anchor="right"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{ sx: { width: { xs: "85%", sm: 380 }, p: 2 } }}
      >
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Request Cart
        </Typography>
        <Divider />
        {cartLines.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            Your cart is empty.
          </Typography>
        ) : (
          <List dense>
            {cartLines.map(({ item, count }) => {
              const key = itemKey(item);
              return (
                <ListItem
                  key={key}
                  disableGutters
                  secondaryAction={
                    <IconButton edge="end" onClick={() => removeLine(key)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={item.itemName}
                    secondary={`${count} × ${currency(item.itemCost)} = ${currency(
                      Number(item.itemCost || 0) * count
                    )}`}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
        <Divider sx={{ my: 1 }} />
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography fontWeight={600}>Estimated Total</Typography>
          <Typography fontWeight={600}>{currency(cartTotal)}</Typography>
        </Box>
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={cartLines.length === 0 || submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Submitting…" : "Submit for Approval"}
        </Button>
      </Drawer>
    </Box>
  );
};

export default PartsCatalogRequest;
